<template>
  <sup v-if="marker"
    ><button
      type="button"
      :aria-label="i18n(translations.label, { numbers: spoken })"
      class="cursor-pointer underline transition-opacity duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:opacity-65"
      @click="handleClick()"
    >
      {{ marker }}
    </button></sup
  >
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { type SourceKey, sourceMarker } from '~/scopes/campaign/data/sources'

import translations from './SourceMarker.translations'

// A block's citation, and the way into the bibliography: on a metal sheet the numbers are
// the client's official ones (see `Source.number`), so they index no local list — every
// marker opens the whole list, which is why no metal renders a bibliography of its own.
//
// The root gates itself, so callers drop it in unconditionally: a block that cites nothing
// renders no element, and nobody resolves the marker twice to decide. Keep comments out of
// the template and the tags glued shut, for the reasons `UI/InlineMarkdown.vue` records.
const props = defineProps<{
  refs?: readonly SourceKey[]
  /** A surface with a numbering of its own (the journeys), printed as given. */
  numbers?: readonly number[]
  /** Tracking surface of the host view — metal prose renders in the panel and the reader. */
  zone: string
}>()

const i18n = useI18n()
const { trackClick } = useTracking()
const { openDoc } = useModalState()

const marker = computed(() =>
  props.numbers?.length
    ? [...props.numbers].sort((a, b) => a - b).join(',')
    : sourceMarker(props.refs),
)

// Spoken with the commas breathing ("sources 3, 25"); the printed form stays tight.
const spoken = computed(() => marker.value.replaceAll(',', ', '))

function handleClick() {
  trackClick({ zone: props.zone, name: 'sources', campaign: 'raw-materials' })
  openDoc('sources')
}
</script>
